/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSidePopout } from 'modules/layout';

import { LoginForm } from 'modules/landingPage';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { clearUserData } from 'modules/landingPage/components/login/redux/slice';
import {
  LoginSelectors,
  LoginThunks,
} from 'modules/landingPage/components/login/redux';
import { InboxPathsEnum } from 'modules/inboxPage/routes';
import { InboxSelectors, InboxThunks } from 'modules/inboxPage/redux';
import { ContactPathsEnum } from 'modules/contactPage/routes';
import { ProfilePathsEnum } from 'modules/profilePage/routes';
import {
  StyledCentralContainer,
  StyledDesktopSubmenu,
  StyledHeader,
  StyledHeaderContentContainer,
  StyledHeaderLogo,
  StyledHeaderLogoSm,
  StyledIconButton,
  StyledIconLink,
  StyledLeftContainer,
  StyledMobileSubmenu,
  StyledRightContainer,
} from './styled';
import {
  BankTouchpointAndCustomerCareAboutUs,
  BankTouchpointAndCustomerCareContacts,
  NavigationAndActionHamburgerMenu,
  NavigationAndActionLogin,
  NavigationAndActionLogout,
  NavigationAndActionMessages,
  NavigationAndActionSearch,
} from '../icons';
import { ProfileItem } from './profileItem';
import { ToggleButton } from '../toggleButton';
import { theme } from '../theme';
import { PopoutTheme } from '../popout/PopoutTheme';
import { HeaderSearch } from '../headerSearch';
import {useTheme} from "styled-components";

export interface HeaderProps {}

export const Header: React.FC<HeaderProps> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const theme = useTheme() as any;

  const user = useAppSelector(LoginSelectors.selectUser);
  const unreadInboxMessageCount = useAppSelector(
    InboxSelectors.selectUnreadMessageCount,
  );

  const [position, setPosition] = useState(window.pageYOffset);
  const [visible, setVisible] = useState(true);

  const { openContainerInPopout, closePopout } = useSidePopout();

  const handleScroll = useCallback(() => {
    const moving = window.pageYOffset;
    setVisible(position > moving);
    setPosition(moving);
  }, [position]);

  useEffect(() => {
    if (user) {
      dispatch(InboxThunks.fetchUnreadMessageCount());
    }
  }, [dispatch, user]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    user && closePopout();
  }, [closePopout, user]);

  const logoutUser = useCallback(() => {
    dispatch(LoginThunks.logout());
    dispatch(clearUserData());
    history.push('/');
  }, [dispatch, history]);

  const handleMenuOpen = () => {
    openContainerInPopout({
      newContainer: user && <StyledMobileSubmenu />,
      theme: PopoutTheme.Dark,
    });
  };

  const handleSearchOpen = () => {
    openContainerInPopout({
      newContainer: user && <HeaderSearch />,
      theme: PopoutTheme.Teal,
    });
  };

  return (
    <>
      <StyledHeader visible={visible} className="full-width-container">
        <StyledHeaderContentContainer>
          <StyledLeftContainer>
            <StyledIconLink aria-label={"Unicredit Leasing"} to="" $desktopOnly>
              <StyledHeaderLogo />
            </StyledIconLink>
            <StyledIconLink aria-label={"Unicredit Leasing"} to="" $mobileOnly>
              <StyledHeaderLogoSm />
            </StyledIconLink>
          </StyledLeftContainer>
          <StyledCentralContainer>
            <ToggleButton
              text={t('header.contact')}
              icon={<BankTouchpointAndCustomerCareContacts />}
              onClick={() => history.push(ContactPathsEnum.contactPage)}
              desktopOnly
              ariaLabel={"Kontakt forma"}
            />
            <ToggleButton
              text={t('header.about')}
              icon={<BankTouchpointAndCustomerCareAboutUs />}
              // eslint-disable-next-line no-return-assign
              onClick={() =>
                window.open(
                  'https://unicreditleasing.hr/home/o-nama/o-nama',
                  '_blank',
                )
              }
              desktopOnly
              ariaLabel={"O nama"}
            />
            {user && (
              <ToggleButton
                text={t('header.search')}
                icon={<NavigationAndActionSearch />}
                onClick={handleSearchOpen}
                noMobileText
              />
            )}
          </StyledCentralContainer>
          <StyledRightContainer>
            {!user && (
              <ToggleButton
                text={t('header.logIn')}
                icon={<NavigationAndActionLogin />}
                onClick={() => {
                  openContainerInPopout({
                    newContainer: <LoginForm />,
                    urlKey: 'login',
                  });
                }}
                color={theme.colors.primaryT2}
                noMobileText
                ariaLabel={"Prijava"}
              />
            )}
            {user && (
              <StyledIconButton
                onClick={() => history.push(ProfilePathsEnum.profilePage)}
                $desktopOnly>
                <ProfileItem user={user} />
              </StyledIconButton>
            )}
            {user && (
              <ToggleButton
                text={t('header.inbox')}
                icon={<NavigationAndActionMessages />}
                onClick={() => history.push(InboxPathsEnum.inboxPage)}
                notificationCount={unreadInboxMessageCount}
                noMobileText
              />
            )}
            {user && (
              <ToggleButton
                text={t('header.logOut')}
                icon={<NavigationAndActionLogout />}
                onClick={logoutUser}
                noMobileText
              />
            )}
            {user && (
              <ToggleButton
                text={t('header.menu')}
                icon={<NavigationAndActionHamburgerMenu />}
                onClick={handleMenuOpen}
                mobileOnly
                noMobileText
              />
            )}
          </StyledRightContainer>
        </StyledHeaderContentContainer>
        {user && <StyledDesktopSubmenu />}
      </StyledHeader>
    </>
  );
};
